import React from "react";
import Axios from "axios";
import { CircularProgress, Pagination } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import TextField from "@mui/material/TextField";
import { useNavigate } from "react-router-dom";
import "./index.css";

const truncate = (text, length = 40) => {
  if (!text) return "";
  return text.length > length ? `${text.slice(0, length)}...` : text;
};

const formatDateTime = (d) => {
  if (!d) return "--";
  return new Date(d).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const initials = (res) => {
  const name = res?.name || `${res?.firstname || ""} ${res?.lastname || ""}`;
  return name.trim().charAt(0).toUpperCase() || "?";
};

export default function AIPitchDeckActivity() {
  const [activity, setactivity] = React.useState([]);
  const [count, setCount] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [search, setSearch] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const [startDate, setStartDate] = React.useState(null);
  const [endDate, setEndDate] = React.useState(null);
  const [includeDeleted, setIncludeDeleted] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const fetchStats = async () => {
    setLoading(true);
    const { data } = await Axios({
      method: "get",
      url: "/api/activity/ai-pitchdecks",
      params: {
        page,
        size: 50,
        search: debouncedSearch,
        start: startDate || "",
        end: endDate || "",
        includeDeleted,
      },
    });
    setactivity(data.data);
    setCount(data.count);
    setLoading(false);
  };
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);
  React.useEffect(() => {
    fetchStats();
  }, [page, debouncedSearch, startDate, endDate, includeDeleted]);
  const navigate = useNavigate();

  return (
    <div className="container admin-user-activity ai-pitchdeck-panel">
      <div className="ai-pd-toolbar">
        <div className="ai-pd-field">
          <p>Search</p>
          <input
            className="ai-pd-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            type="text"
            placeholder="Search by topic or user"
          />
        </div>
        <div className="ai-pd-field">
          <p>Start Date</p>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              value={startDate}
              onChange={(newValue) => {
                setPage(1);
                setStartDate(newValue);
              }}
              format="dd MMM yyyy"
              renderInput={(params) => (
                <TextField {...params} variant="outlined" size="small" />
              )}
            />
          </LocalizationProvider>
        </div>
        <div className="ai-pd-field">
          <p>End Date</p>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              value={endDate}
              onChange={(newValue) => {
                setPage(1);
                setEndDate(newValue);
              }}
              format="dd MMM yyyy"
              renderInput={(params) => (
                <TextField {...params} variant="outlined" size="small" />
              )}
            />
          </LocalizationProvider>
        </div>
        <label className="ai-pd-toggle">
          <input
            type="checkbox"
            checked={includeDeleted}
            onChange={(e) => {
              setPage(1);
              setIncludeDeleted(e.target.checked);
            }}
          />
          Show only deleted decks
        </label>
      </div>
      {loading && <CircularProgress size={24} />}
      <div className="ai-pd-card">
        <table>
          <thead>
            <tr>
              <th>User</th>
              <th>Topic</th>
              <th style={{ textAlign: "center" }}>Slides</th>
              <th>{includeDeleted ? "Deleted On" : "Last Updated"}</th>
            </tr>
          </thead>
          <tbody>
            {activity.length > 0 ? (
              activity.map((res, i) => (
                <tr
                  key={res._id || i}
                  className={res.isDeleted ? "ai-pd-row-deleted" : ""}
                >
                  <td>
                    <div
                      className="ai-pd-user"
                      onClick={() =>
                        navigate(
                          `/admin/user-activity?tab=13&userid=${res.userId?._id}`
                        )
                      }
                    >
                      {res.userId?.avatar ? (
                        <img
                          className="ai-pd-avatar"
                          src={res.userId.avatar}
                          alt=""
                        />
                      ) : (
                        <div className="ai-pd-avatar-fallback">
                          {initials(res.userId)}
                        </div>
                      )}
                      <div>
                        <div className="ai-pd-user-name">
                          {res.userId?.name ||
                            `${res.userId?.firstname || ""} ${
                              res.userId?.lastname || ""
                            }`}
                        </div>
                        <div className="ai-pd-user-email">
                          {res.userId?.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td title={res.topic}>{truncate(res.topic)}</td>
                  <td style={{ textAlign: "center" }}>
                    <span className="ai-pd-slides">{res.slideCount}</span>
                  </td>
                  <td>
                    {formatDateTime(
                      res.isDeleted ? res.deletedAt : res.updatedAt
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4}>
                  <div className="ai-pd-empty">No AI Pitch Decks Found</div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="pagination">
        <Pagination
          count={count ? Math.ceil(count / 50) : 0}
          page={page}
          onChange={(e, v) => setPage(v)}
        />
      </div>
    </div>
  );
}
